import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CurrencyEur, Percent, Package, Info, Snowflake } from "@phosphor-icons/react";

import VatConfigSection from "@/components/pricing/VatConfigSection";
const eur = (c) => `€${((c || 0) / 100).toFixed(2)}`;

/**
 * Kunden-spezifische Preise: Overrides, Rabatt, HUB-Distanz, Preisstopp.
 * Wird eingebunden im Customer-Dialog (Tab "Preise" oder inline).
 * Props: customer (Kunde), tenant (Mandant mit plans), onSaved(customer)
 */
export default function CustomerPricingSubPanel({ customer, tenant, onSaved }) {
  const [overrides, setOverrides] = useState({});
  const [priceFrozen, setPriceFrozen] = useState(false);
  const [discount, setDiscount] = useState({ type: "percent", value: 0, mode: "recurring", valid_until: "", note: "" });
  const [hub, setHub] = useState({ enabled: false, distance_meters: 0 });
  const [vat, setVat] = useState({ exempt: false, article: "", note: "", reverse_charge: false, vat_id: "" });
  const [vatCheck, setVatCheck] = useState(null); // {valid, name, address, checked_at, error}
  const [vatChecking, setVatChecking] = useState(false);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [selPlan, setSelPlan] = useState("");

  useEffect(() => {
    const cp = customer?.custom_pricing || {};
    setOverrides(cp.plan_price_overrides || {});
    setPriceFrozen(!!cp.price_frozen);
    const d = customer?.discount || {};
    setDiscount({
      type: d.type || "percent",
      value: d.value || 0,
      mode: d.mode || "recurring",
      valid_until: d.valid_until || "",
      note: d.note || "",
    });
    const h = customer?.hub_service || {};
    setHub({ enabled: !!h.enabled, distance_meters: h.distance_meters || 0 });
    const v = customer?.vat_config || {};
    setVat({
      exempt: !!v.exempt,
      article: v.article || "",
      note: v.note || "",
      reverse_charge: !!v.reverse_charge,
      vat_id: v.vat_id || "",
    });
    // Persistiertes VIES-Ergebnis anzeigen
    if (v.last_validation) {
      setVatCheck({ ...v.last_validation, persisted: true });
    } else {
      setVatCheck(null);
    }
    if (tenant?.plans?.length) setSelPlan(tenant.plans[0].plan_id);
  }, [customer, tenant]);

  const loadQuote = useCallback(async (planId) => {
    try {
      const r = await api.get(`/customers/${customer.customer_id}/quote`, { params: { plan_id: planId } });
      setQuote(r.data);
    } catch (e) { setQuote(null); }
  }, [customer?.customer_id]);
  useEffect(() => { if (selPlan) loadQuote(selPlan); }, [selPlan, loadQuote]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/customers/${customer.customer_id}/pricing`, {
        custom_pricing: { plan_price_overrides: overrides, price_frozen: priceFrozen },
        discount: discount.value > 0 ? discount : null,
        hub_service: hub,
        vat_config: vat,
      });
      onSaved?.(r.data);
      setMsg({ ok: true, msg: "Gespeichert" });
      loadQuote(selPlan);
      setTimeout(() => setMsg(null), 2000);
    } catch (e) {
      setMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" });
    } finally { setBusy(false); }
  };

  const plans = tenant?.plans || [];

  return (
    <div className="space-y-5" data-testid="customer-pricing-subpanel">
      {/* Overrides pro Plan */}
      <section>
        <h4 className="text-sm font-bold tracking-tight mb-2 flex items-center gap-2">
          <CurrencyEur size={14} weight="bold" /> Individuelle Preise
        </h4>
        <p className="text-xs text-zinc-500 mb-3">Leer lassen = Standardpreis. Nur Cent-Beträge (z.B. 1490 für 14,90 €).</p>
        <div className="space-y-2">
          {plans.map((p) => (
            <div key={p.plan_id} className="flex items-center gap-3">
              <div className="flex-1 text-sm">
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-zinc-500">Standard: {eur(p.price_per_cleaning_cents)}</div>
              </div>
              <Input
                type="number"
                className="h-9 w-32 rounded-none font-mono text-sm"
                placeholder={String(p.price_per_cleaning_cents)}
                value={overrides[p.plan_id] || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  const newO = { ...overrides };
                  if (v === "" || v === "0") delete newO[p.plan_id];
                  else newO[p.plan_id] = parseInt(v);
                  setOverrides(newO);
                }}
                data-testid={`override-${p.plan_id}`}
              />
              <span className="text-xs text-zinc-400 w-20">
                {overrides[p.plan_id] ? eur(overrides[p.plan_id]) : "—"}
              </span>
            </div>
          ))}
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={priceFrozen}
            onChange={(e) => setPriceFrozen(e.target.checked)}
            className="w-4 h-4"
            data-testid="price-frozen-checkbox"
          />
          <Snowflake size={14} weight="bold" className="text-blue-600" />
          <span>Preise beibehalten (bei Massen-Preiserhöhungen überspringen)</span>
        </label>
      </section>

      {/* Rabatt */}
      <section>
        <h4 className="text-sm font-bold tracking-tight mb-2 flex items-center gap-2">
          <Percent size={14} weight="bold" /> Rabatt
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <div>
            <Label className="text-xs">Art</Label>
            <Select value={discount.type} onValueChange={(v) => setDiscount({ ...discount, type: v })}>
              <SelectTrigger className="h-9 rounded-none" data-testid="discount-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">Prozent (%)</SelectItem>
                <SelectItem value="fixed">Festbetrag (€)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Wert</Label>
            <Input
              type="number"
              step={discount.type === "percent" ? "0.1" : "0.01"}
              min={0}
              className="h-9 rounded-none font-mono"
              value={discount.value}
              onChange={(e) => setDiscount({ ...discount, value: parseFloat(e.target.value) || 0 })}
              data-testid="discount-value-input"
            />
          </div>
          <div>
            <Label className="text-xs">Häufigkeit</Label>
            <Select value={discount.mode} onValueChange={(v) => setDiscount({ ...discount, mode: v })}>
              <SelectTrigger className="h-9 rounded-none" data-testid="discount-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recurring">Dauerhaft</SelectItem>
                <SelectItem value="once">Einmalig</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Gültig bis (optional)</Label>
            <Input
              type="date"
              className="h-9 rounded-none"
              value={discount.valid_until || ""}
              onChange={(e) => setDiscount({ ...discount, valid_until: e.target.value })}
              data-testid="discount-valid-input"
            />
          </div>
        </div>
        <Input
          className="h-9 rounded-none mt-2 text-sm"
          placeholder='Notiz (z.B. „Treuerabatt Stammkunde")'
          value={discount.note}
          onChange={(e) => setDiscount({ ...discount, note: e.target.value })}
          data-testid="discount-note-input"
        />
      </section>

      {/* HUB */}
      <section>
        <h4 className="text-sm font-bold tracking-tight mb-2 flex items-center gap-2">
          <Package size={14} weight="bold" /> Hol- & Bringservice (HUB)
        </h4>
        <p className="text-xs text-zinc-500 mb-3">
          Distanz vom öffentlichen Standplatz zur Tonne. Preis wird automatisch anhand der Tenant-Konfiguration berechnet.
          Der Fahrer bestätigt vor Ort in seiner App.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex items-center gap-2 h-9">
            <input
              type="checkbox"
              checked={hub.enabled}
              onChange={(e) => setHub({ ...hub, enabled: e.target.checked })}
              className="w-5 h-5"
              data-testid="hub-enabled-cb"
            />
            <span className="text-sm">HUB gebucht</span>
          </label>
          <div>
            <Label className="text-xs">Distanz (m)</Label>
            <Input
              type="number"
              min={0}
              className="h-9 rounded-none w-28"
              value={hub.distance_meters}
              onChange={(e) => setHub({ ...hub, distance_meters: parseInt(e.target.value) || 0 })}
              disabled={!hub.enabled}
              data-testid="hub-distance-input"
            />
          </div>
          {customer?.hub_service?.computed_price_cents > 0 && (
            <div className="text-sm">
              Preis (gespeichert): <strong>{eur(customer.hub_service.computed_price_cents)}</strong>
            </div>
          )}
        </div>
      </section>

      <VatConfigSection customer={customer} vat={vat} setVat={setVat} vatCheck={vatCheck} setVatCheck={setVatCheck} vatChecking={vatChecking} setVatChecking={setVatChecking} onSaved={onSaved} />
      {/* Live-Quote */}
      <section className="p-4 rounded bg-gradient-to-br from-zinc-50 to-white border border-black/10">
        <h4 className="text-sm font-bold tracking-tight mb-2 flex items-center gap-2">
          <Info size={14} weight="bold" /> Kalkulation (aktueller Stand)
        </h4>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={selPlan} onValueChange={setSelPlan}>
            <SelectTrigger className="h-9 w-48 rounded-none" data-testid="quote-plan-select"><SelectValue placeholder="Plan wählen" /></SelectTrigger>
            <SelectContent>
              {plans.map((p) => <SelectItem key={p.plan_id} value={p.plan_id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {quote && (
            <div className="text-xs space-y-0.5 flex-1 min-w-[300px] font-mono" data-testid="quote-breakdown">
              <div className="flex justify-between"><span>Basis {quote.was_overridden && "(Sonderpreis)"}</span><span>{eur(quote.base_cents)}</span></div>
              {quote.discount_cents > 0 && <div className="flex justify-between text-emerald-700"><span>− Rabatt</span><span>−{eur(quote.discount_cents)}</span></div>}
              {quote.fuel_surcharge_cents > 0 && <div className="flex justify-between"><span>+ Treibstoff</span><span>+{eur(quote.fuel_surcharge_cents)}</span></div>}
              {quote.hub_cents > 0 && <div className="flex justify-between"><span>+ HUB</span><span>+{eur(quote.hub_cents)}</span></div>}
              <div className="flex justify-between border-t border-black/10 pt-1 mt-1 font-bold text-sm"><span>Total</span><span>{eur(quote.total_cents)}</span></div>
            </div>
          )}
        </div>
      </section>

      <div className="flex items-center justify-end gap-3">
        {msg && <span className={`text-xs font-medium ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.ok ? "✓" : "✗"} {msg.msg}</span>}
        <Button onClick={save} disabled={busy} className="rounded-none bg-black text-white hover:bg-zinc-800" data-testid="save-customer-pricing-btn">
          {busy ? "Speichere…" : "Preise speichern"}
        </Button>
      </div>
    </div>
  );
}
